import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Auth service
export const authService = {
  register: async (userData) => {
    const response = await axios.post(`${API}/auth/register`, userData);
    if (response.data.access_token) {
      localStorage.setItem("token", response.data.access_token);
      localStorage.setItem("user", JSON.stringify(response.data.user));
      axios.defaults.headers.common["Authorization"] = `Bearer ${response.data.access_token}`;
    }
    return response.data;
  },

  login: async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    if (response.data.access_token) {
      localStorage.setItem("token", response.data.access_token);
      localStorage.setItem("user", JSON.stringify(response.data.user));
      axios.defaults.headers.common["Authorization"] = `Bearer ${response.data.access_token}`;
    }
    return response.data;
  },

  logout: () => {
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    delete axios.defaults.headers.common["Authorization"];
  },

  getToken: () => {
    return localStorage.getItem("token");
  },

  getUser: () => {
    const user = localStorage.getItem("user");
    return user ? JSON.parse(user) : null;
  },

  isAuthenticated: () => {
    return !!localStorage.getItem("token");
  },

  getCurrentUser: async () => {
    const response = await axios.get(`${API}/auth/me`);
    localStorage.setItem("user", JSON.stringify(response.data));
    return response.data;
  }
};

// Property service
export const propertyService = {
  getAll: async () => {
    const response = await axios.get(`${API}/properties`);
    return response.data;
  },

  getMyProperties: async () => {
    const response = await axios.get(`${API}/properties/my`);
    return response.data;
  },

  getById: async (id) => {
    const response = await axios.get(`${API}/properties/${id}`);
    return response.data;
  },

  getBySlug: async (slug) => {
    // Pass auth token if available (for draft property preview by owner)
    const token = localStorage.getItem('token');
    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    const response = await axios.get(`${API}/properties/slug/${slug}`, { headers });
    return response.data;
  },

  create: async (propertyData) => {
    const response = await axios.post(`${API}/properties`, propertyData);
    return response.data;
  },

  update: async (id, propertyData) => {
    const response = await axios.put(`${API}/properties/${id}`, propertyData);
    return response.data;
  },

  delete: async (id) => {
    const response = await axios.delete(`${API}/properties/${id}`);
    return response.data;
  },

  uploadImages: async (propertyId, files) => {
    const formData = new FormData();
    files.forEach(file => {
      formData.append("files", file);
    });
    const response = await axios.post(
      `${API}/properties/${propertyId}/images`,
      formData,
      { headers: { "Content-Type": "multipart/form-data" } }
    );
    return response.data;
  },

  uploadMainImage: async (propertyId, file) => {
    const formData = new FormData();
    formData.append("file", file);
    const response = await axios.post(
      `${API}/properties/${propertyId}/main-image`,
      formData,
      { headers: { "Content-Type": "multipart/form-data" } }
    );
    return response.data;
  },

  deleteMainImage: async (propertyId) => {
    const response = await axios.delete(`${API}/properties/${propertyId}/main-image`);
    return response.data;
  },

  deleteImage: async (propertyId, imageUrl) => {
    const response = await axios.delete(
      `${API}/properties/${propertyId}/images`,
      { data: { image_url: imageUrl } }
    );
    return response.data;
  }
};

// AI Generation Service (Pro+)
export const aiService = {
  generateDescription: async (payload) => {
    const response = await axios.post(`${API}/ai/generate-description`, payload);
    return response.data.text;
  },
  generateTitles: async (payload) => {
    const response = await axios.post(`${API}/ai/generate-titles`, payload);
    return response.data.titles || [];
  },
  generateWhatsApp: async (payload) => {
    const response = await axios.post(`${API}/ai/generate-whatsapp`, payload);
    return response.data.text;
  }
};

// Lead service
export const leadService = {
  create: async (leadData) => {
    const response = await axios.post(`${API}/leads`, leadData);
    return response.data;
  },

  getAll: async (propertyId = null) => {
    const params = propertyId ? { property_id: propertyId } : {};
    const response = await axios.get(`${API}/leads`, { params });
    return response.data;
  },

  markAsRead: async (leadId) => {
    const response = await axios.put(`${API}/leads/${leadId}/read`);
    return response.data;
  }
};

// Subscription service
export const subscriptionService = {
  getPlans: async () => {
    const response = await axios.get(`${API}/plans`);
    return response.data;
  },

  getStatus: async () => {
    const response = await axios.get(`${API}/subscription/status`);
    return response.data;
  },

  checkSubscription: async () => {
    const response = await axios.get(`${API}/subscription/check`);
    return response.data;
  },

  getSubscriptionDetails: async () => {
    const response = await axios.get(`${API}/payments/subscription`);
    return response.data;
  },

  openBillingPortal: async () => {
    const response = await axios.post(`${API}/payments/billing-portal`);
    if (response.data?.url) {
      window.location.href = response.data.url;
    }
    return response.data;
  }
};

// Setup axios interceptor for auth token
const token = authService.getToken();
if (token) {
  axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
}

// Response interceptor for handling 401 errors
axios.interceptors.response.use(
  (response) => response,
  (error) => {
    // Don't redirect on login/register 401 errors - let the form handle them
    // Don't redirect on public endpoints (properties, slug lookups)
    const url = error.config?.url || "";
    const isAuthEndpoint = url.includes("/auth/login") || url.includes("/auth/register");
    const isPublicEndpoint = url.includes("/properties/slug/") || url.includes("/leads");
    
    if (error.response?.status === 401 && !isAuthEndpoint && !isPublicEndpoint) {
      authService.logout();
      window.location.href = "/login";
    }
    return Promise.reject(error);
  }
);
